/**
 * Verificación en dos pasos (TOTP) del asesor en el portal.
 *
 * La API trata al asesor como staff (rol de sistema `consultant`, ver
 * `api/src/services/seguridad/dosPasos.js`): las rutas de cotizaciones, levantamientos y consultas
 * le exigen la 2FA. Sin ella contesta
 *   - 403 `STAFF_2FA_NO_ACTIVADO`: la cuenta aún no la activó;
 *   - 428 `STAFF_2FA_REQUERIDO`: la tiene, pero la petición no trae la sesión 2FA;
 *   - 401 `STAFF_2FA_SESION_INVALIDA`: la sesión caducó (dura 12 h) o es de otra cuenta.
 *
 * El portal no tenía dónde activarla ni mandaba la sesión, así que el asesor no podía guardar ni
 * una cotización. Ahora el interceptor de `services/api.js` para la petición, abre el diálogo de la
 * 2FA y, con la sesión obtenida, la repite: el asesor no pierde lo que estaba escribiendo.
 *
 * Sin dependencias (ni React ni axios) para poder probarlo con `node --test`.
 */

export const CLAVE_SESION_2FA = 'partner_sesion2fa'
export const CABECERA_SESION_2FA = 'X-Sesion-2FA'

const CODIGOS_ACTIVAR = new Set(['STAFF_2FA_NO_ACTIVADO'])
const CODIGOS_VERIFICAR = new Set(['STAFF_2FA_REQUERIDO', 'STAFF_2FA_SESION_INVALIDA'])

// Respuestas de la 2FA que NO son una sesión caducada del portal: un 401 con cualquiera de estos
// códigos no debe echar al asesor al login (un código mal tecleado lo sacaba del portal).
const CODIGOS_PROPIOS = new Set([
  ...CODIGOS_ACTIVAR,
  ...CODIGOS_VERIFICAR,
  'TOTP_INVALID',
  'CUENTA_2FA_LIMITE',
  'EQUIPO_TOTP_NO_ACTIVADO',
])

const codigoDe = (response) => response?.data?.code

/** `/v1/auth/2fa/*`: las rutas con las que se activa y se verifica; nunca se reintentan. */
export function esRutaDeDosPasos(url = '') {
  return String(url).includes('/auth/2fa/')
}

/** 'activar' | 'verificar' | null — qué le falta a la cuenta según la respuesta de la API. */
export function motivoDosPasos(response) {
  const code = codigoDe(response)
  if (CODIGOS_ACTIVAR.has(code)) return 'activar'
  if (CODIGOS_VERIFICAR.has(code)) return 'verificar'
  return null
}

/** ¿Es un error de la 2FA (y no una sesión del portal caducada)? */
export function esErrorDeDosPasos(response, url = '') {
  return CODIGOS_PROPIOS.has(codigoDe(response)) || esRutaDeDosPasos(url)
}

/**
 * Una sola petición de 2FA en vuelo. Al abrir una cotización salen varias llamadas a la vez y
 * todas fallan igual: deben esperar al MISMO diálogo, no abrir uno cada una.
 *
 * `solicitar(motivo)` devuelve una promesa que se resuelve con `completar()` (hay sesión: repetir)
 * y se rechaza con `cancelar()` (el asesor cerró el diálogo).
 */
export function crearSolicitudDosPasos() {
  let pendiente = null
  const oyentes = new Set()
  const avisar = () => oyentes.forEach((fn) => fn(pendiente ? pendiente.motivo : null))

  const cerrar = (accion, valor) => {
    const p = pendiente
    pendiente = null
    avisar()
    if (p) p[accion](valor)
  }

  return {
    solicitar(motivo) {
      if (pendiente) {
        // «Activar» manda sobre «verificar»: sin 2FA activada no hay código que pedir.
        if (motivo === 'activar' && pendiente.motivo !== 'activar') {
          pendiente.motivo = 'activar'
          avisar()
        }
        return pendiente.promesa
      }
      let resolver
      let rechazar
      const promesa = new Promise((res, rej) => {
        resolver = res
        rechazar = rej
      })
      pendiente = { motivo, promesa, resolver, rechazar }
      avisar()
      return promesa
    },
    completar: () => cerrar('resolver'),
    cancelar: () => cerrar('rechazar', new Error('Verificación en dos pasos cancelada')),
    motivo: () => (pendiente ? pendiente.motivo : null),
    suscribir(fn) {
      oyentes.add(fn)
      return () => oyentes.delete(fn)
    },
  }
}

/**
 * Qué hacer con un error del API por la 2FA.
 *
 * Devuelve `null` si el error no es de la 2FA (el interceptor sigue con su manejo de siempre), o
 * la promesa de la petición repetida con la sesión nueva. Si el asesor cancela, la promesa se
 * rechaza con el error ORIGINAL: la pantalla enseña el mensaje de la API como antes.
 */
export function manejarErrorDosPasos(error, { solicitud, borrarSesion, repetir }) {
  const config = error?.config
  const motivo = motivoDosPasos(error?.response)
  if (!motivo || !config || config._reintento2fa || esRutaDeDosPasos(config.url)) return null

  // Una sesión caducada se tira: si no, la próxima petición la volvería a mandar.
  if (codigoDe(error.response) === 'STAFF_2FA_SESION_INVALIDA') borrarSesion()

  return solicitud.solicitar(motivo).then(
    () => {
      config._reintento2fa = true
      return repetir(config)
    },
    () => Promise.reject(error),
  )
}
